import { DocumentNode, gql } from '@apollo/client';
import {
  Button,
  CloseButton,
  Heading,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  Stack,
  useToast,
} from '@chakra-ui/react';
import * as React from 'react';
import { FormProvider, useForm } from 'react-hook-form';

import { useUpdateAttributes } from '@/hooks/useUpdateAttributes';
import {
  ATTRIBUTE_FRAGMENT,
  EditableAttribute,
  Preview,
} from '@/modules/attributes';
import { AdminEdit } from '@/modules/attributes';
import { notEmpty } from '@/utilities/not-empty';

import { CompanyInfoEdit_OrganizationFragment } from './graphql-types/CompanyInfoEdit_OrganizationFragment';

type FormValues = {
  attributes: EditableAttribute[];
};

export const CompanyInfoEdit: React.FC<{
  organization?: CompanyInfoEdit_OrganizationFragment | null;
  isOpen: boolean;
  onClose: () => void;
}> & { organizationFragment: DocumentNode } = ({
  organization,
  isOpen,
  onClose,
}) => {
  const toast = useToast();

  const profileAttributeResults = organization?.profileAttributes?.results ?? [];

  const ALLOWED_LABELS = [
  'Company Name',
  'Organization Name',
  'Website',
  'Email',
  'Primary Phone',
  // 'Street Address',
  // 'City',
  // 'State',
  // 'Zip Code',
  // 'Country',
];

  const defaultValues = React.useMemo<FormValues>(() => ({
  attributes: profileAttributeResults
    .map(({ attribute }) => {
      if (!ALLOWED_LABELS.includes(attribute.label)) {
        return null;
      }

      switch (attribute.__typename) {
        case 'FreeTextAttribute':
          return { ...attribute, value: attribute.freeTextValue };

        case 'SingleSelectAttribute':
          return { ...attribute, value: attribute.singleSelectValue };

        case 'MultiSelectAttribute':
          return { ...attribute, value: attribute.multiSelectValue };

        default:
          return null;
      }
    })
    .filter(notEmpty),
}), [profileAttributeResults]);

  const methods = useForm<FormValues>({
    defaultValues,
  });

  const {
    handleSubmit,
    formState: { isSubmitting },
    reset,
  } = methods;

  React.useEffect(() => {
    if (isOpen) {
      reset(defaultValues);
    }
  }, [isOpen, defaultValues, reset]);

  const { updateAttributes } = useUpdateAttributes({
    idOfOrganizationToRefetch: organization?._id,
  });

  const onSubmit = handleSubmit(async (data) => {
    try {
      await updateAttributes({
        attributes: data.attributes
          .filter((attribute) => attribute.canCurrentUserEditValue)
          .map((attribute) => ({
            attributeId: attribute._id,
            value: attribute.value,
          })),
      });

      onClose();
    } catch {
      toast({
        status: 'error',
        description: 'An Unexpected Error Occurred',
      });
    }
  });

  return (
    <>
      <Modal
        isOpen={isOpen}
        onClose={onClose}
        size="xl"
        closeOnOverlayClick={!isSubmitting}
      >
        <ModalOverlay />
        <ModalContent>
          <ModalHeader borderBottom="1px solid" borderColor="#D4D4D4">Edit Organization Info </ModalHeader>
        <ModalCloseButton />
          <ModalBody pt="4">
            <FormProvider {...methods}>
              <AdminEdit
                isViewingOwnProfile={
                  organization?.currentUserOrganizationPermission?.canManageOrganizationProfile ?? false
                }
                twoColumn={true}
                hideAllow={false}
              />
            </FormProvider>
          </ModalBody>

          <ModalFooter gap="3">
            <Button
              variant="outline"
              onClick={() => {
                reset(defaultValues);
                onClose();
              }}
              isDisabled={isSubmitting} w="120px"
              borderColor="primary.500"
            >
              Cancel
            </Button>

            <Button onClick={onSubmit} isLoading={isSubmitting} w="120px">
              Save
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>
    </>
  );
};

CompanyInfoEdit.organizationFragment = gql`
  fragment CompanyInfoEdit_OrganizationFragment on Organization {
    _id
    displayName
    profileAttributes(input: {}) {
      results {
        attribute {
          _id
          label
          ...AttributeFragment
        }
      }
    }
    currentUserOrganizationPermission {
      canManageOrganizationProfile
    }
  }
  ${ATTRIBUTE_FRAGMENT}
`;